import { InlineAlertType } from '@varo/graphql-schema-types';
import { Image } from 'expo-image';
/* eslint-disable @typescript-eslint/no-require-imports */
import { UnknownInputParams, useRouter } from 'expo-router';
import IconBulletList from 'features/v2a/receiver/components/IconBulletList/IconBulletList';
import ReceiverFooter from 'features/v2a/receiver/components/ReceiverFooter/ReceiverFooter';
import ReceiverHeader from 'features/v2a/receiver/components/ReceiverHeader/ReceiverHeader';
import {
  buildSignUpUrlParams,
  decodeJWT,
  ErrorViewType,
} from 'features/v2a/receiver/helpers/V2A.helpers';
import usePaymentLanding from 'features/v2a/receiver/hooks/usePaymentLanding';
import { useReceiverSavingsRates } from 'features/v2a/receiver/hooks/useReceiverSavingsRates';
import { V2AReceiverContext } from 'features/v2a/receiver/providers/V2AReceiverContextProvider';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from 'shared/components/vxl/Button/Button';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import Illustration from 'shared/components/vxl/Illustration/Illustration';
import { InlineAlert } from 'shared/components/vxl/InlineAlert/InlineAlert';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpressiveLgText from 'shared/components/vxl/typography/expressive/ExpressiveLgText';
import ExpressiveXSText from 'shared/components/vxl/typography/expressive/ExpressiveXSText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodySmBoldText from 'shared/components/vxl/typography/primary/body/BodySmBoldText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import TitleSmText from 'shared/components/vxl/typography/primary/title/TitleSmText';
import { SMALL_SCREEN_MAX_WIDTH } from 'shared/constants/layout';
import { isWeb } from 'shared/constants/platform';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { useAuth } from 'shared/providers/auth';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

const ReceiverLanding = () => {
  const router = useRouter();
  const [externalId, setExternalId] = useState<string>();
  const [linkSource, setLinkSource] = useState<string>('');
  const { paymentDetails } = useContext(V2AReceiverContext);
  const { isLoggedIn } = useAuth();

  const insets = useSafeAreaInsets();

  if (isLoggedIn && router.canDismiss()) {
    router.dismiss();
  }

  const { t: translatedText } = useTranslation('moveMoney', {
    keyPrefix: 'receiverLanding',
  });

  const { loading, tokenResponse, tokenResponseError } = usePaymentLanding();
  const {
    rates: savingsRates,
    isUnavailable: areSavingsRatesUnavailable,
    refetch: refetchSavingsRates,
  } = useReceiverSavingsRates();

  const bullets = [
    {
      text: translatedText('header.bullets.items.0'),
      icon: 'MONEY',
    },
    {
      text: translatedText('header.bullets.items.1'),
      icon: 'BANK',
    },
    {
      text: translatedText('header.bullets.items.2'),
      icon: 'EARLY_PAYDAY',
    },
  ];

  const signupUrlParams = (): UnknownInputParams | undefined => {
    const signUpPageUrlParams =
      paymentDetails && buildSignUpUrlParams(paymentDetails);
    return signUpPageUrlParams;
  };

  useViewDidFocusScreenAnalyticsEvent({
    key: 'v2aReceiverLandingPage',
    customAttributes: {
      receiver_id: externalId ?? '',
      link_source: linkSource,
    },
  });

  const logReceiverFlowInitiatedEvent = usePressableAnalytics({
    analyticsId: 'v2aReceiverFlowInitiated',
    customAttributes: {
      receiver_id: externalId,
    },
  });

  const logErrorAnalytics = usePressableAnalytics({
    analyticsId: 'v2aReceiverErrorEncountered',
    customAttributes: {
      receiver_id: externalId,
      error_name: tokenResponseError?.error,
      error_description: tokenResponseError?.subtitle,
    },
  });

  useEffect(() => {
    if (tokenResponseError) {
      logErrorAnalytics();
    }
  }, [tokenResponseError, logErrorAnalytics]);

  useEffect(() => {
    if (tokenResponse) {
      const decoded: Record<string, string> = decodeJWT(tokenResponse);
      const userId = decoded?.rid as string;
      setExternalId(userId);
      setLinkSource(decoded?.rcc.toLowerCase());
    }
  }, [tokenResponse]);

  useEffect(() => {
    if (externalId) {
      logReceiverFlowInitiatedEvent();
    }
  }, [externalId, logReceiverFlowInitiatedEvent]);

  const onSubmit = () => {
    router.push({
      pathname: '/payments-detail',
    });
  };

  const incentiveAlertText = translatedText('header.incentiveAlert');

  return (
    <>
      <View
        style={[
          styles.container,
          {
            paddingBottom: insets.bottom,
            paddingTop: insets.top,
          },
        ]}
      >
        <ScrollView style={styles.scrollContainer}>
          <View style={styles.container}>
            <ReceiverHeader />
            <View style={styles.illustrationContainer}>
              <Illustration
                illustration="PLANE_A_ANIMATED"
                accessibilityLabel="plane animation"
              />
            </View>
            <View style={styles.headerContainer}>
              <ExpressiveLgText style={styles.headerText} numberOfLines={0}>
                {translatedText('header.title', {
                  name: tokenResponse?.name,
                  amount: tokenResponse?.amount,
                })}
              </ExpressiveLgText>
              {tokenResponse?.note ? (
                <TitleSmText numberOfLines={0}>
                  {`"${tokenResponse.note}"`}
                </TitleSmText>
              ) : null}
              <BodyBaseText style={styles.headerText} numberOfLines={0}>
                {translatedText('header.line', { name: tokenResponse?.name })}
              </BodyBaseText>
            </View>
            <View style={styles.ctaContainer}>
              <Button
                label={translatedText('header.button')}
                onPress={() => {
                  router.dismissTo({
                    pathname: '/signup',
                    params: signupUrlParams(),
                  });
                }}
                analyticsId="v2aReceiverSignupClicked"
                customAttributes={{ receiver_id: externalId }}
              />
              <Button
                label={translatedText('header.secondaryCTA')}
                type="TEXT"
                onPress={onSubmit}
                analyticsId="transferToDifferentBankClicked"
                customAttributes={{ receiver_id: externalId }}
              />
              <InlineAlert
                alertType={InlineAlertType.Brand}
                bodyText={incentiveAlertText}
                iconKeyType="GIFT"
              />
            </View>
            <View style={styles.bulletsContainer}>
              <BodySmBoldText numberOfLines={0}>
                {translatedText('header.bullets.title')}
              </BodySmBoldText>
              {bullets.map((bullet) => (
                <IconBulletList
                  key={bullet.icon}
                  text={bullet.text}
                  icon={bullet.icon}
                />
              ))}
            </View>
            <View style={styles.paperTear}>
              <Image
                source={require('assets/images/vxl/textures/paper-tear-a.png')}
                accessibilityLabel="varo-logo"
                accessibilityRole="image"
                role="img"
                contentFit="cover"
                style={styles.image}
              />
            </View>
            <View style={styles.landingCopy}>
              <ExpressiveXSText style={styles.copyTitle} numberOfLines={0}>
                {translatedText('marketing.title')}
              </ExpressiveXSText>
            </View>
            <View style={styles.benefitsContainer}>
              <ListItem
                primaryCopy={translatedText('marketing.benefits.0.title')}
                primaryNumberOfLines={0}
                secondaryCopy={translatedText('marketing.benefits.0.subtitle')}
                secondaryNumberOfLines={0}
                colorTheme="transparent"
                showsChevron={false}
                boldPrimaryCopy
                leadingAccessory={{
                  id: 'no-hidden-fees-icon-avatar',
                  name: VXLComponentId.ICON_AVATAR,
                  props: {
                    variant: 'LG',
                    illustrationKey: 'CASH_B',
                  },
                }}
              />
              {savingsRates ? (
                <>
                  <HairLineDivider />
                  <ListItem
                    primaryCopy={translatedText('marketing.benefits.1.title', {
                      elevatedApy: savingsRates.elevatedApy,
                    })}
                    primaryNumberOfLines={0}
                    secondaryCopy={translatedText(
                      'marketing.benefits.1.subtitle',
                      {
                        compactBalanceCap: savingsRates.compactBalanceCap,
                      },
                    )}
                    secondaryNumberOfLines={0}
                    colorTheme="transparent"
                    showsChevron={false}
                    boldPrimaryCopy
                    leadingAccessory={{
                      id: 'apy-icon-avatar',
                      name: VXLComponentId.ICON_AVATAR,
                      props: {
                        variant: 'LG',
                        illustrationKey: 'PIGGY_BANK_A',
                      },
                    }}
                  />
                </>
              ) : null}
              <HairLineDivider />
              <ListItem
                primaryCopy={translatedText('marketing.benefits.2.title')}
                primaryNumberOfLines={0}
                secondaryCopy={translatedText('marketing.benefits.2.subtitle')}
                secondaryNumberOfLines={0}
                colorTheme="transparent"
                showsChevron={false}
                boldPrimaryCopy
                leadingAccessory={{
                  id: 'fdic-icon-avatar',
                  name: VXLComponentId.ICON_AVATAR,
                  props: {
                    variant: 'LG',
                    illustrationKey: 'BANK_A',
                  },
                }}
              />
            </View>
            <View style={styles.loveVaroContainer}>
              <ExpressiveXSText numberOfLines={0} style={styles.eggplantText}>
                {translatedText('marketing.loveVaro.title')}
              </ExpressiveXSText>
              <View style={styles.loveVaroContentContainer}>
                <View style={styles.loveVaroReviewContainer}>
                  <View style={styles.loveVaroScoreContainer}>
                    <ExpressiveXSText
                      style={styles.eggplantText}
                      numberOfLines={0}
                    >
                      {translatedText('marketing.loveVaro.iosScore')}
                    </ExpressiveXSText>
                    <Icon
                      iconKey="STAR_FILLED"
                      iconColor={theme.color.expressive.eggplant}
                    />
                  </View>
                  <BodySmText style={styles.eggplantText} numberOfLines={0}>
                    {translatedText('marketing.loveVaro.iosReviews')}
                  </BodySmText>
                </View>
                <View style={styles.loveVaroReviewContainer}>
                  <View style={styles.loveVaroScoreContainer}>
                    <ExpressiveXSText
                      style={styles.eggplantText}
                      numberOfLines={0}
                    >
                      {translatedText('marketing.loveVaro.androidScore')}
                    </ExpressiveXSText>
                    <Icon
                      iconKey="STAR_FILLED"
                      iconColor={theme.color.expressive.eggplant}
                    />
                  </View>
                  <BodySmText style={styles.eggplantText} numberOfLines={0}>
                    {translatedText('marketing.loveVaro.androidReviews')}
                  </BodySmText>
                </View>
              </View>
              <View style={styles.loveVaroImageContainer}>
                <Image
                  source={require('assets/images/receiverLanding/loveVaroIllustration.png')}
                  contentFit="contain"
                  accessibilityLabel="love varo illustration"
                  accessibilityRole="image"
                  role="img"
                  style={styles.image}
                />
              </View>
            </View>
          </View>
          <ReceiverFooter
            savingsRates={savingsRates}
            isSavingsRatesUnavailable={areSavingsRatesUnavailable}
            onRetrySavingsRates={refetchSavingsRates}
          />
        </ScrollView>
      </View>
      <ScreenTransitionOverlay
        isLoading={loading || (!tokenResponse && !tokenResponseError)}
        LoadingComponent={
          <View style={styles.loader}>
            <LoadingSpinner />
          </View>
        }
        ErrorComponent={
          tokenResponseError?.type === ErrorViewType.TERMINAL ? (
            <ErrorScreen
              messageHeadline={tokenResponseError.error}
              messageBody={tokenResponseError.subtitle}
              screenType={
                tokenResponseError.screenType || ErrorScreenType.Error
              }
              screenAnalyticsEventKey="v2aReceiverFlowError"
              customAttributes={{ receiver_id: externalId ?? '' }}
            />
          ) : null
        }
      />
    </>
  );
};

const styles = StyleSheet.create({
  benefitsContainer: {
    backgroundColor: theme.color.expressive.orchid,
    paddingBottom: theme.spacing.xl,
    paddingHorizontal: theme.spacing.m,
  },
  bulletsContainer: {
    gap: theme.spacing.m,
    marginTop: theme.spacing.xl,
    paddingHorizontal: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  copyTitle: {
    color: theme.color.expressive.eggplant,
    marginBottom: theme.spacing.m,
    marginTop: theme.spacing.xl,
  },
  ctaContainer: {
    gap: theme.spacing.s,
    marginTop: theme.spacing.xl,
    paddingHorizontal: theme.spacing.m,
  },
  eggplantText: {
    color: theme.color.expressive.eggplant,
  },
  headerContainer: {
    alignItems: 'center',
    gap: theme.spacing.l,
    marginTop: theme.spacing.xl,
    paddingHorizontal: theme.spacing.m,
  },
  headerText: {
    textAlign: 'center',
  },
  illustrationContainer: {
    alignSelf: 'center',
    height: 210,
    width: 343,
  },
  image: {
    flex: 1,
    width: '100%',
  },
  landingCopy: {
    backgroundColor: theme.color.expressive.orchid,
    paddingHorizontal: theme.spacing.m,
  },
  loader: {
    alignSelf: 'center',
    height: 24,
    width: 24,
  },
  loveVaroContainer: {
    alignItems: 'center',
    backgroundColor: theme.color.expressive.lavender,
    gap: theme.spacing.xl,
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.xxl,
  },
  loveVaroContentContainer: {
    flexDirection: 'row',
    gap: theme.spacing.l,
  },
  loveVaroImageContainer: {
    aspectRatio: 155 / 86,
    overflow: 'hidden',
    width: '100%',
  },
  loveVaroReviewContainer: {
    alignItems: 'center',
    gap: theme.spacing.s,
  },
  loveVaroScoreContainer: {
    flexDirection: 'row',
    gap: 6,
  },
  paperTear: {
    aspectRatio: 343 / 20,
    backgroundColor: theme.color.expressive.orchid,
    marginTop: theme.spacing.xl,
    overflow: 'hidden',
    width: '100%',
  },
  scrollContainer: {
    backgroundColor: theme.color.background.primary,
    ...(isWeb && {
      alignSelf: 'center',
      maxWidth: SMALL_SCREEN_MAX_WIDTH,
      width: '100%',
    }),
  },
});

export default ReceiverLanding;
